import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { ProductList } from '@modules/products';
import { useCustomSet, useRemoveProductsFromCustomSet } from '@modules/custom-sets';
import { Breadcrumbs } from '@components';
import { Button, DoubleCheckModal } from '@shared/ui';
import '@shared/styles/page.css';
import './CustomSetProductsPage.css';

export default function CustomSetProductsPage() {
    const { id } = useParams<{ id: string }>();
    const setId = id ? parseInt(id, 10) : undefined;

    const { data: customSet, isLoading: customSetLoading, error: customSetError, refetch: refetchCustomSet } = useCustomSet(setId!);

    const products = customSet?.products
    const removeProducts = useRemoveProductsFromCustomSet();
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [selectedProductIds, setSelectedProductIds] = useState<Set<number>>(new Set());
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    const handleToggleSelection = () => {
        setIsSelectionMode(!isSelectionMode);
        setSelectedProductIds(new Set());
    };

    const handleSelectProduct = (productId: number, selected: boolean) => {
        const newSelected = new Set(selectedProductIds);
        if (selected) {
            newSelected.add(productId);
        } else {
            newSelected.delete(productId);
        }
        setSelectedProductIds(newSelected);
    };

    const handleDeleteSelected = () => {
        if (selectedProductIds.size === 0) {
            notifications.show({
                title: 'Внимание',
                message: 'Выберите товары для удаления',
                color: 'yellow',
            });
            return;
        }

        setIsDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        try {
            await removeProducts.mutateAsync({
                id: setId!,
                productIds: Array.from(selectedProductIds),
            });
            setSelectedProductIds(new Set());
            setIsSelectionMode(false);
            setIsDeleteModalOpen(false);

            notifications.show({
                title: 'Успешно',
                message: 'Товары удалены из подборки',
                color: 'green',
            });
        } catch (error) {
            console.error('Ошибка при удалении товаров:', error);
            notifications.show({
                title: 'Ошибка',
                message: 'Ошибка при удалении товаров из подборки',
                color: 'red',
            });
        }
    };

    const cancelDelete = () => {
        setIsDeleteModalOpen(false);
    };

    return (
        <div className="page-container">
            <Breadcrumbs items={[
                { label: 'Главная', path: '/' },
                { label: 'Подборки', path: '/sets' },
                { label: customSet?.name || 'Подборка' }
            ]} />

            <div className="page-header">
                <div className="page-header-top">
                    <h1>{customSet?.name || 'Подборка'}</h1>
                    {!isSelectionMode ? (
                        <Button onClick={handleToggleSelection}>
                            Удалить из подборки
                        </Button>
                    ) : (
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <Button onClick={handleToggleSelection} variant='secondary'>
                                Отмена
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleDeleteSelected}
                                disabled={selectedProductIds.size === 0}
                            >
                                Удалить ({selectedProductIds.size})
                            </Button>
                        </div>
                    )}
                </div>
            </div>



            <div className="content-wrapper">
                <ProductList
                    products={products || []}
                    isLoading={customSetLoading}
                    error={customSetError}
                    refetch={refetchCustomSet}
                    isSelectionMode={isSelectionMode}
                    selectedProductIds={selectedProductIds}
                    onSelect={handleSelectProduct}
                    hideMenu={true}
                    emptyMessage="Товары в подборке не найдены"
                />
            </div>

            <DoubleCheckModal
                isOpen={isDeleteModalOpen}
                onClose={cancelDelete}
                title="Удалить товары?"
                message={`Вы уверены, что хотите удалить ${selectedProductIds.size} товар(ов) из подборки?`}
                secondButtonText="Удалить"
                secondButtonAction={confirmDelete}
                isLoading={removeProducts.isPending}
            />
        </div>
    );
}

